/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { TranslateService } from '@ngx-translate/core';
import { Entity } from './entity.interface';
import { EntityService } from './entity-service.interface';
import { ConfirmationService, LazyLoadEvent, ToastMessageOptions, MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';

export class ListPage<T extends Entity> {
  items: T[] = [];
  totalRecords = 0;
  deleteItemMsg: string | undefined = undefined;
  initialLoadQuery: LazyLoadEvent = {
    first: 0,
    rows: 10,
  };

  constructor(
    protected readonly entityService: EntityService<T>,
    protected readonly translateService: TranslateService,
    protected readonly messageService: MessageService,
    protected readonly confirmationService: ConfirmationService,
    private readonly deleteEntityMsgKey?: string
  ) {
    // initialize with internationalized delete message
    if (deleteEntityMsgKey) {
      translateService.stream(this.deleteEntityMsgKey).subscribe((res: string) => {
        this.deleteItemMsg = res;
      });
    }
  }

  loadLazy(
    event?: TableLazyLoadEvent,
    successCallback?: (data: object) => void,
    errorCallback?: (data: object) => void
  ) {
    if (!event) {
      event = this.initialLoadQuery;
    }
    this.entityService
      .getEntityList(event as LazyLoadEvent)
      .subscribe(
        successCallback ? successCallback : this.loadDataSuccessDefaultCallback,
        errorCallback ? errorCallback : this.loadDataErrorDefaultCallBack
      );
  }

  delete(item: T, callback?: () => void) {
    this.confirmationService.confirm({
      message: this.deleteItemMsg,
      accept: () => {
        this.entityService.deleteEntity(item.uuid).subscribe(() => callback && callback());
      },
    });
  }

  protected loadDataSuccessDefaultCallback = (data: object) => {
    this.items = data['_embedded']['entityModels'] || data['_embedded']['entityModelList'];
    this.totalRecords = data['page']['totalElements'];
  };
  /* LOAD DATA end */

  protected loadDataErrorDefaultCallBack = () => {
    this.addMessage({
      severity: 'error',
      summary: 'Error retrieving data from server!',
    });
  };

  protected addMessage(message: ToastMessageOptions) {
    if (message) {
      this.messageService.add(message);
    }
  }
}
